/**
 * Quiet Hours voice — pre-generated TTS playback for guided sessions.
 *
 * Voice MP3 files are generated at build time by `tools/voice-pipeline`,
 * one file per (category, duration) pair. The app loads them at session
 * start, plays them once, and syncs the breathing visual to their slow
 * cadence.
 *
 * No runtime API calls. If a file is missing (e.g., voice-pipeline has
 * never been run), `playVoice()` returns `null` and the caller falls back
 * to an unguided session.
 *
 * Mute respect: voice playback honors the `muted` Preferences setting via
 * the same localStorage signal that `juice/audio.ts` reads.
 */

import { audio } from '@/juice';

import type { QuietHoursCategorySlug, DurationMinutes } from './categories';

/**
 * A single spoken utterance in a guided script. The voice-pipeline
 * makes ONE TTS call per line (with `timestamps: true`) and then glues
 * the resulting MP3s together with `pauseAfterMs` milliseconds of
 * silence between them. The cue file written next to the MP3 lists
 * each line's exact start/end in the concatenated track.
 *
 * Eleven v3 silently drops SSML `<break/>` tags, so chunked generation
 * with hand-authored silence is the only reliable way to enforce
 * pause durations.
 */
export interface GuidedScriptLine {
  /** Plain English. Sent to TTS verbatim. */
  text: string;
  /** Silence in milliseconds appended after this line. */
  pauseAfterMs: number;
}

/**
 * One guided script — for a single (category, duration). The voice
 * field is the ElevenLabs voice name used by the pipeline; only the
 * pipeline reads it, but it lives with the script so reviewers can see
 * the chosen narrator without leaving the file.
 */
export interface GuidedScript {
  durationMin: DurationMinutes;
  /** ElevenLabs voice name (e.g., 'Matilda', 'Sarah', 'Laura'). */
  voice: string;
  lines: GuidedScriptLine[];
}

/**
 * A cue line in the rendered voice track. `startSec` and `endSec` are
 * the absolute times (into the final MP3) at which this line begins
 * and ends being spoken. The line text is repeated here so consumers
 * can subtitle without re-loading the script.
 */
export interface VoiceCueLine {
  text: string;
  startSec: number;
  endSec: number;
}

/**
 * The cue manifest written alongside each voice MP3 by the pipeline.
 * Lives at `voice/<category>/<duration>.cues.json` and is loaded by
 * the session player to drive the orb's breath cycle against the
 * voice's actual timing.
 */
export interface VoiceCueFile {
  /** Total wall-clock duration of the MP3 in seconds. */
  durationSec: number;
  /** Pre-roll silence the pipeline added before the first spoken line. */
  preRollSec: number;
  /** Each spoken line and its start/end in the final track. */
  lines: VoiceCueLine[];
}

/** Public URL of the pre-generated MP3 for a (category, duration). */
export function voiceFileUrl(
  category: QuietHoursCategorySlug,
  duration: DurationMinutes,
): string {
  return `/generated/quiet-hours/voice/${category}/${duration}.mp3`;
}

/** Public URL of the cue file written next to the voice MP3. */
export function voiceCueUrl(
  category: QuietHoursCategorySlug,
  duration: DurationMinutes,
): string {
  return `/generated/quiet-hours/voice/${category}/${duration}.cues.json`;
}

/**
 * Probe whether the voice file actually exists at the public URL. We do
 * a HEAD request; if it 404s, the caller should fall back to unguided.
 * Results are cached per URL for the lifetime of the tab.
 */
const probeCache = new Map<string, boolean>();
export async function voiceFileExists(
  category: QuietHoursCategorySlug,
  duration: DurationMinutes,
): Promise<boolean> {
  const url = voiceFileUrl(category, duration);
  if (probeCache.has(url)) return probeCache.get(url)!;
  try {
    const res = await fetch(url, { method: 'HEAD' });
    const ok = res.ok;
    probeCache.set(url, ok);
    return ok;
  } catch {
    probeCache.set(url, false);
    return false;
  }
}

/** Load the cue manifest for a (category, duration). Returns null on miss. */
export async function loadVoiceCues(
  category: QuietHoursCategorySlug,
  duration: DurationMinutes,
): Promise<VoiceCueFile | null> {
  try {
    const res = await fetch(voiceCueUrl(category, duration));
    if (!res.ok) return null;
    return (await res.json()) as VoiceCueFile;
  } catch {
    return null;
  }
}

/**
 * A live voice playback handle. Returned by {@link playVoice}. The caller
 * is responsible for calling `stop()` when the session ends. Exposes the
 * underlying HTMLAudioElement so the parent can read `currentTime`
 * (the cue-driven orb needs this) and pause/resume in sync with the
 * session phase.
 */
export interface VoicePlayback {
  stop(): void;
  pause(): void;
  resume(): void;
  /** Underlying audio element — for `currentTime`-driven sync. */
  readonly el: HTMLAudioElement;
  /** Returns true once the file has fully played. */
  readonly ended: boolean;
}

/**
 * Begin guided-session voice playback. Returns a {@link VoicePlayback}
 * handle, or `null` if the file is missing / muted / un-playable.
 *
 * Voice plays through a plain `<audio>` element (not Web Audio) so the
 * session can read `audio.currentTime` cheaply each frame for cue sync.
 * The element's volume is the voice level; the music bed runs at lower
 * gain so the voice sits above without overpowering.
 */
export function playVoice(
  category: QuietHoursCategorySlug,
  duration: DurationMinutes,
  opts: { volume?: number } = {},
): VoicePlayback | null {
  if (audio.isMuted()) return null;
  if (typeof window === 'undefined') return null;

  const url = voiceFileUrl(category, duration);
  const el = new Audio(url);
  el.preload = 'auto';
  el.volume = opts.volume ?? 1.0;
  el.crossOrigin = 'anonymous';

  let ended = false;
  el.addEventListener('ended', () => {
    ended = true;
  });

  void el.play().catch(() => {
    // Autoplay can fail if there was no user gesture; the Begin button
    // is a gesture so this should normally succeed. If it fails, we
    // just don't have voice — silence + music is still a valid session.
    ended = true;
  });

  // Debug hook for screenshot tooling — exposes the current voice
  // element under window.__qhVoiceEl so playwright can read currentTime.
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    (window as unknown as { __qhVoiceEl?: HTMLAudioElement }).__qhVoiceEl = el;
  }

  return {
    el,
    stop(): void {
      try {
        el.pause();
        el.src = '';
      } catch {
        /* ignore */
      }
      ended = true;
    },
    pause(): void {
      try {
        el.pause();
      } catch {
        /* ignore */
      }
    },
    resume(): void {
      try {
        void el.play();
      } catch {
        /* ignore */
      }
    },
    get ended(): boolean {
      return ended;
    },
  };
}
